import React, { useContext, useEffect, useState, useRef } from 'react';
import AuthHeader from '../../commons/AuthHeader';
import AuthFooter from '../../commons/AuthFooter';
import { Button, ButtonVariant, IconName, ButtonIconAlign, Icon, IconSize } from '@eightfold.ai/octuple';
import { globalContext } from '../../store';
import { launchAssessmentWithStatusUpdate, initialiseImmersiveAssessment } from '../../commons/commonFunctions';
import { useNavigate } from 'react-router-dom';
import { actionButtonStyle } from '../../commons/commonStyles';
import Api from '../../Api';
import ReactPlayer from 'react-player';
import Loader from '../../commons/Loader';

const GenerateActionItems = ({ isDocumentBriefingCompleted, isVideoCompleted, immersiveAssessData, navigate, showLoader, setShowLoader, dispatch }: any) => {
  return (
    <div className='assessmentActionItems'>
      <Button
        text={immersiveAssessData?.assessmentStatus === 'N' ? 'Launch Assessment' : 'Continue Assessment'}
        variant={ButtonVariant.Secondary}
        classNames={(!isDocumentBriefingCompleted || !isVideoCompleted) && !immersiveAssessData?.prepWorkCompleted ? 'actionButton inactive' : 'actionButton'}
        style={actionButtonStyle}
        onClick={() => launchAssessmentWithStatusUpdate(immersiveAssessData, setShowLoader, dispatch)}
        iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
        alignIcon={ButtonIconAlign.Right}
        disabled={(!isDocumentBriefingCompleted || !isVideoCompleted) && !immersiveAssessData?.prepWorkCompleted}
      />
    </div>
  );
};

const Introduction = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  const title = globalState?.clientData?.name;
  const [showLoader, setShowLoader] = useState(false);
  const [videoURL, setVideoURL] = useState<any>('');
  const [, setLeaderName] = useState<any>('');
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [, setHeaderImage] = useState<string>('');
  const [, setPrimaryColor] = useState<string>('#BA3A19');
  const [isDocumentBriefingCompleted, setIsDocumentBriefingCompleted] = useState<boolean>(false);
  const [showIntroModal, setShowIntroModal] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string>('');
  const [isVideoCompleted, setIsVideoCompleted] = useState(false);
  const [showPdfDownloadLoader, setShowPdfDownloadLoader] = useState(false);
  const [playing, setPlaying] = useState(true);
  const playerRef = useRef<any>(null);
  const prepData = globalState?.immersiveAssessmentData?.prepData;
  const immersiveAssessData = globalState?.immersiveAssessmentData;
  useEffect(() => {
    const params = { globalState, dispatch, navigate, setLeaderName, setHeaderImage, setHeaderLogo, setPrimaryColor, title };
    initialiseImmersiveAssessment(params);
  }, []);

  useEffect(() => {
    if (isVideoCompleted && isDocumentBriefingCompleted && !immersiveAssessData?.prepWorkCompleted) {
      Api.updatePrepWorkCompletedFlag(immersiveAssessData?.assessmentId)
    }
  }, [isVideoCompleted, isDocumentBriefingCompleted, immersiveAssessData?.prepWorkCompleted]);

  const handleWatchVideo = () => {
    if (videoURL) {
      setVideoError('');
      setShowIntroModal(true);
      setPlaying(true);
      setIsVideoCompleted(true);
      return;
    }

    Api.getVideoURL(prepData?.videoAssetPath)
      .then(response => {
        if (response?.success) {
          const signed = response?.response?.data?.signedUrl;
          setVideoURL(signed);
          setVideoError('');
          setShowIntroModal(true);
          setPlaying(true);
          setIsVideoCompleted(true);
        } else {
          setShowLoader(false);
        }
      })
      .catch((err: any) => {
        setShowLoader(false);
      });
  };

  const handleDownloadBriefing = () => {
    const pdfUrlId: any = prepData?.preReadPdfAssetPath;
    setShowPdfDownloadLoader(true);
    Api.downloadImmersivePdf(pdfUrlId)
      .then((res: any) => {
        if (res?.success) {
          setShowPdfDownloadLoader(false);
          setIsDocumentBriefingCompleted(true);
        } else {
          setShowPdfDownloadLoader(false);
        }
      })
      .catch((err: any) => {
        setShowPdfDownloadLoader(false);
        if (err?.code === 401 && err?.message === 'Unauthorized Access') {
          dispatch({
            type: 'SET_SHOW_LOGOUT_MODAL',
            payload: true,
          });
        }
      });
  };

  const closeModal = () => {
    setShowIntroModal(false);
  };

  const handleEnded = () => {
    setIsVideoCompleted(true);
    setPlaying(false);
  };

  const handleSeek = (seconds: any) => {
    if (seconds.target.duration - seconds.target.currentTime <= 1) {
      handleEnded();
    }
  };

  const handlePlayerError = (e: any) => {
    setVideoError('Unable to play this video.');
  };

  const styleObject: React.CSSProperties = {
    cursor: showPdfDownloadLoader ? 'not-allowed' : 'pointer',
    pointerEvents: showPdfDownloadLoader ? 'none' : 'auto',
    opacity: showPdfDownloadLoader ? 0.6 : 1,
    boxShadow: '4px 4px 10px 2px rgba(0, 0, 0, 0.4)',
  };

  const IconPath = showPdfDownloadLoader ? IconName.mdiLoading : IconName.mdiArrowRight;
  const isPrepWorkCompleted = isDocumentBriefingCompleted || immersiveAssessData?.prepWorkCompleted;
  const isVideoOrPrepWorkCompleted = isVideoCompleted || immersiveAssessData?.prepWorkCompleted;
  let videoContent: React.ReactNode;

  if (videoError) {
    videoContent = <div className='video-error-message'>{videoError}</div>;
  } else if (videoURL) {
    videoContent = (
      <ReactPlayer
        ref={playerRef}
        src={videoURL}
        onEnded={handleEnded}
        onError={handlePlayerError}
        onSeeking={handleSeek}
        playing={playing}
        controls={true}
        width='100%'
        height='100%'
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
    );
  } else {
    videoContent = <Loader />;
  }

  const handleDocumentBriefing = () => {
    if(prepData?.preReadAssetUrl) {
      setIsDocumentBriefingCompleted(true)
      window.open(prepData?.preReadAssetUrl, '_blank');
    }
    else{ 
      if(!showPdfDownloadLoader) handleDownloadBriefing();
    }
  }


  return (
    <>
      <div className='mainWrapper'>
        <div className='innerWrapper'>
          <AuthHeader title={title} headerLogo={headerLogo} setLoader={setShowLoader} />
          <div className='middleWrapper'>
            <div className='leftTextWrapper'>
              <div>
                <Button
                  text='Back to dashboard'
                  variant={ButtonVariant.Secondary}
                  classNames='completedAssessmentButton'
                  onClick={() => navigate('/dashboard')}
                  transparent={true}
                  iconProps={{ path: IconName.mdiChevronLeft }}
                  alignIcon={ButtonIconAlign.Left}
                />
              </div>
              <div className='textContainer'>
                <h1 className='textHeading'>Welcome to the {prepData?.assessmentTitle}</h1>
                <h2 className='textDescription'>
                  We're excited to invite you to participate in the Heidrick & Struggles {prepData?.assessmentTitle}. Before you begin, we want to help you understand what to
                  expect from this unique and engaging experience.{' '}
                </h2>
                <GenerateActionItems
                  isDocumentBriefingCompleted={isDocumentBriefingCompleted}
                  isVideoCompleted={isVideoCompleted}
                  immersiveAssessData={immersiveAssessData}
                  navigate={navigate}
                  showLoader={showLoader}
                  setShowLoader={setShowLoader}
                  dispatch={dispatch}
                />
              </div>
            </div>

            <div className='rightTextWrapper'>
              <div className='bgImageWrapper' style={{ backgroundImage: `url(./../assets/img/immersiveBackground.svg)` }}>
                <div className='contentOverlay'>
                  <div className='Textcontent'>
                    <div className='textSection'>
                      <h3 className='sectionHeading' style={{marginBottom:'0px'}}>Before taking the assessment</h3>
                      <h4 className='preAssessmentInstruction'>Please click both cards below to complete the required pre-assessment activities. </h4>
                      <div className='sectionDescription'>
                        <div
                          className={isVideoOrPrepWorkCompleted ? 'sectionCard sectionCompleted' : 'sectionCard'}
                          onClick={handleWatchVideo}
                          style={{ cursor: 'pointer', boxShadow: '4px 4px 10px 2px rgba(0, 0, 0, 0.4)' }}
                          aria-label='Watch informational video'
                        >
                          <div className='cardIconWrapper' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {isVideoOrPrepWorkCompleted ? (
                              <>
                                <span className='iconWrapperCompleted'>
                                  <Icon path={IconName.mdiCheck} size={IconSize.Small} style={{ color: '#fff' }} />
                                </span>
                                <Icon path={IconName.mdiArrowRight} size={IconSize.Medium} style={{ color: '#002136' }} />
                              </>
                            ) : (
                              <>
                                <img src={'../../assets/img/playIcon.svg'} alt='video-thumbnail' className='cardIcon' />
                                <Icon path={IconName.mdiArrowRight} size={IconSize.Medium} style={{ color: '#fff' }} />
                              </>
                            )}
                          </div>
                          <div className='agilityCardTitle' style={isVideoOrPrepWorkCompleted ? { color: '#002136' } : {}}>
                            Watch informational video
                          </div>
                          <div className='agilityCardDescription'>
                            The short information video provides you with context about the entire immersive {prepData?.assessmentType?.toLowerCase()} experience.
                          </div>
                        </div>
                        <div
                          className={isPrepWorkCompleted ? 'sectionCard sectionCompleted' : 'sectionCard'}
                          role='button'
                          onClick={() => {
                            handleDocumentBriefing()
                           
                          }}
                          style={{ ...styleObject }}
                          aria-label='Download briefing document'
                          aria-disabled={showPdfDownloadLoader}
                        >
                          <div className='cardIconWrapper' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {isPrepWorkCompleted ? (
                              <>
                                <span className='iconWrapperCompleted'>
                                  <Icon path={IconName.mdiCheck} size={IconSize.Small} style={{ color: '#fff' }} />
                                </span>
                                <Icon
                                  path={IconPath}
                                  spin={showPdfDownloadLoader}
                                  size={IconSize.Medium}
                                  style={{ color: '#002136' }}
                                />
                              </>
                            ) : (
                              <>
                                <img src={'../../assets/img/checkList.svg'} alt='check List' className='cardIcon' />
                                <Icon
                                  path={IconPath}
                                  spin={showPdfDownloadLoader}
                                  size={IconSize.Medium}
                                  style={{ color: '#fff' }}
                                />
                              </>
                            )}
                          </div>
                          <div className='agilityCardTitle' style={isPrepWorkCompleted ? { color: '#002136' } : {}}>
                            {prepData?.assessmentType.toLowerCase() === 'cxo' ? 'Review briefing document' : 'Download and review briefing document'}
                          </div>
                          <div className='agilityCardDescription'>
                            This provides context for your simulation and will be crucial to your performance and decision-making during the experience.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='textSection'>
                      <h3 className='sectionHeading'>What to know before you start</h3>
                      <div className='sectionDescription'>
                        <div className='listSection'>
                          <div className='listTitle'>Time & Environment</div>
                          <div className='listItem'>
                            <Icon path={IconName.mdiClockOutline} size={IconSize.Small} />
                            <div className='listText'>
                              Duration: <span>{prepData?.durationText}</span>
                            </div>
                          </div>
                          <hr className='listItemSeparator' />

                          <div className='listItem'>
                            <img src={'../../assets/img/countOne.svg'} alt='count' />
                            <div className='listText'>
                              Must be completed <span>in one sitting</span>
                            </div>
                          </div>
                          <hr className='listItemSeparator' />

                          <div className='listItem'>
                            <img src={'../../assets/img/mute.svg'} alt='mute' />
                            <div className='listText'>
                              Join from a <span>quiet location</span>
                            </div>
                          </div>
                        </div>

                        <div className='listSection'>
                          <div className='listTitle'>Technical Requirements</div>
                          <div className='listItem'>
                            <img src={'../../assets/img/desktop.svg'} alt='desktop' />
                            <div className='listText'>
                              <span>Desktop/laptop</span> only
                            </div>
                          </div>
                          <hr className='listItemSeparator' />

                          <div className='listItem'>
                            <img src={'../../assets/img/microPhone.svg'} alt='microphone' />
                            <div className='listText'>
                              Working <span>microphone required</span>
                            </div>
                          </div>
                          <hr className='listItemSeparator' />

                          <div className='listItem'>
                            <img src={'../../assets/img/headphones.svg'} alt='headphones' />
                            <div className='listText'>
                              <span>Headphones recommended</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AuthFooter />
        </div>
      </div>

      {showIntroModal && (
        <div onClick={closeModal} className='video-modal-overlay'>
          <div onClick={e => e.stopPropagation()} className='video-modal-container'>
            <button onClick={() => { closeModal();}} className='video-modal-close-button'>
              <span>✕</span>
            </button>
            <div className='video-modal-video-wrapper'>
              {videoContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Introduction;
